<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";
import { Check } from "@lucide/vue";

import { LoginForm } from "@/features";

const { t, tm, rt } = useI18n();

const brandProps = computed(() => tm("auth.brand.props") as string[]);
</script>

<template>
  <div
    data-testid="login-page"
    class="grid min-h-dvh lg:grid-cols-2"
  >
    <!-- ЛІВО: форма на чистому фоні (референс RapidAPI). Бренд живе на панелі,
         форма стартує з h1 (name-agnostic, rename-040). -->
    <section class="flex items-center justify-center bg-background p-6 sm:p-10">
      <div class="flex w-full max-w-sm flex-col gap-8">
        <!-- Заголовок -->
        <h1 class="text-2xl font-semibold tracking-tight">
          {{ t("auth.login.title") }}
        </h1>

        <!-- Форма (email · password+forgot · remember · submit) -->
        <LoginForm />

        <!-- Футер: реєстрація + підтримка читаються як один блок -->
        <div class="flex flex-col gap-2">
          <!-- Перехід на реєстрацію -->
          <p class="text-sm text-muted-foreground">
            {{ t("auth.login.noAccount") }}
            <RouterLink
              to="/signup"
              class="rounded-sm font-medium text-primary underline-offset-4 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              {{ t("auth.login.signUp") }}
            </RouterLink>
          </p>

          <!-- Футер підтримки (mailto) -->
          <p class="text-sm text-muted-foreground">
            {{ t("auth.login.supportPrompt") }}
            <a
              href="mailto:support@salonos.app"
              class="rounded-sm font-medium text-primary underline-offset-4 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              {{ t("auth.login.supportLink") }}
            </a>
          </p>
        </div>
      </div>
    </section>

    <!-- ПРАВО: бренд-панель (декоративна) на бренд-градієнті. Ховається на мобільному.
         Шари (z-order): --gradient-brand → glow → scrim → контент. -->
    <aside
      aria-hidden="true"
      class="login-hero relative hidden flex-col justify-end overflow-hidden p-12 lg:flex"
    >
      <div
        class="login-hero__glow"
        aria-hidden="true"
      />
      <div
        class="login-hero__scrim"
        aria-hidden="true"
      />

      <!-- Контент знизу: заголовок + підзаголовок + 3 value-props -->
      <div class="relative z-10 flex flex-col gap-4">
        <h2 class="text-2xl font-semibold tracking-tight">
          {{ t("auth.brand.title") }}
        </h2>
        <p class="login-hero__subtitle max-w-md">
          {{ t("auth.brand.subtitle") }}
        </p>
        <ul class="mt-2 flex flex-col gap-3">
          <li
            v-for="(prop, idx) in brandProps"
            :key="idx"
            class="flex items-start gap-3"
          >
            <span class="login-hero__check flex size-5 shrink-0 items-center justify-center rounded-full">
              <Check
                class="size-3.5"
                :stroke-width="3"
              />
            </span>
            <span class="login-hero__prop max-w-md text-sm">{{ rt(prop) }}</span>
          </li>
        </ul>
      </div>
    </aside>
  </div>
</template>

<style scoped>
.login-hero {
  /* єдиний центр кольору тексту панелі — бренд-ADR (024) перевизначає тут */
  --login-hero-fg: #fff;
  background: var(--gradient-brand);
  color: var(--login-hero-fg);
}

.login-hero__subtitle {
  color: color-mix(in oklch, var(--login-hero-fg), transparent 20%);
}

.login-hero__prop {
  color: color-mix(in oklch, var(--login-hero-fg), transparent 10%);
}

/* glow (фініш B) — м'який radial-світ вгорі-справа */
.login-hero__glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at 85% 12%,
    rgba(255, 255, 255, 0.24),
    transparent 48%
  );
  pointer-events: none;
}

/* scrim — гарантує контраст білого тексту знизу (AA) проти найсвітлішої точки */
.login-hero__scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.28), transparent 58%);
  pointer-events: none;
}

.login-hero__check {
  background: rgba(255, 255, 255, 0.16);
  color: var(--login-hero-fg);
}
</style>
